import { useEffect, useId, useRef, useState } from "react";

import { countryName } from "../countries.js";
import { control, narrowed, resting } from "./controls.js";
import { Flag } from "./Flag.js";

/**
 * Choosing a country, with the flag showing.
 *
 * ## Why this is not a `<select>`
 *
 * Because a `<select>` cannot show one. The browser renders `<option>` as text
 * and nothing else — no image, no SVG, no markup — and that is a rule of the
 * platform rather than a gap in the styling. The flags are wanted here for the
 * same reason they are wanted in the list: a cellar of French and Italian wine
 * is sorted by country in the head before it is sorted by anything else, and a
 * shape is faster to find than a word.
 *
 * ## What that costs, stated rather than hidden
 *
 * A native select on a phone opens the operating system's own wheel — a large,
 * familiar, one-thumb control that no custom list matches. This screen is used
 * standing in front of a cabinet, so that is a real loss and not a theoretical
 * one. It is accepted here and nowhere else: the shelf and format pickers in
 * `BottlePlacement` stay native, because they have nothing to show that text
 * cannot say.
 *
 * The loss is paid down rather than shrugged at. Every row is a full-height
 * touch target, the button is the same height as every other field, and the
 * keyboard behaviour below is a `<select>`'s: arrows move, Home and End jump,
 * a letter jumps to the countries starting with it, Enter takes the highlighted
 * one, Escape leaves without changing anything. A control that looks like a
 * field and then fails under the keyboard is worse than the text it replaced.
 */
export function CountrySelect({
  id,
  value,
  onChange,
  options,
  anyLabel,
  preferred = 0,
}: {
  id: string;
  /** The chosen code, or null — which only exists when `anyLabel` is given. */
  value: string | null;
  onChange: (value: string | null) => void;
  options: readonly string[];
  /** "Alla länder", for the filter. Omitted where an answer is required. */
  anyLabel?: string;
  /** How many of `options` are the reader's own countries, drawn above a
   *  line — see `orderCountries`. */
  preferred?: number;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);

  // null first when it is offered, because "all countries" is where the filter
  // starts and the place it is returned to.
  const choices: (string | null)[] =
    anyLabel === undefined ? [...options] : [null, ...options];
  const label = (choice: string | null) =>
    choice === null ? (anyLabel ?? "") : countryName(choice);

  // Opening lands on what is already chosen rather than at the top, so the
  // keyboard starts where the eye does.
  function show() {
    setActive(Math.max(0, choices.indexOf(value)));
    setOpen(true);
  }

  // A click anywhere else closes it. Pointerdown rather than click, so the
  // press that lands on another control does not also count as a choice here.
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  // Keep the highlighted row in view when the arrows walk past the edge.
  //
  // Guarded rather than called straight, because scrolling is a convenience and
  // not the control: jsdom has no `scrollIntoView` at all, and an unguarded call
  // turns "the list did not scroll" into "the country cannot be chosen".
  useEffect(() => {
    if (!open) return;
    const row = list.current?.children[active];
    if (row instanceof HTMLElement && typeof row.scrollIntoView === "function") {
      row.scrollIntoView({ block: "nearest" });
    }
  }, [open, active]);

  function choose(index: number) {
    onChange(choices[index] ?? null);
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (!open) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
        event.preventDefault();
        show();
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => Math.min(i + 1, choices.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(choices.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(active);
        break;
      case "Escape":
        event.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        setOpen(false);
        break;
      default: {
        // Typeahead, as a select has. One letter, first match after the
        // current row so repeated presses cycle rather than stick.
        if (event.key.length !== 1) return;
        const letter = event.key.toLowerCase();
        const order = [
          ...choices.slice(active + 1).map((_, i) => active + 1 + i),
          ...choices.slice(0, active + 1).map((_, i) => i),
        ];
        const hit = order.find((i) =>
          label(choices[i] ?? null)
            .toLowerCase()
            .startsWith(letter),
        );
        if (hit !== undefined) setActive(hit);
      }
    }
  }

  const set = value !== null;

  return (
    <div className="relative" ref={root}>
      <button
        type="button"
        id={id}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        className={`${control} flex items-center gap-2 pr-9 text-left ${
          set ? narrowed : resting
        }`}
      >
        {value !== null && <Flag countryCode={value} />}
        <span className="truncate">{label(value)}</span>
        <svg
          viewBox="0 0 10 6"
          aria-hidden="true"
          className="pointer-events-none absolute right-3 h-1.5 w-2.5 fill-none stroke-current"
          strokeWidth="1.5"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <ul
          id={listId}
          ref={list}
          role="listbox"
          aria-label={anyLabel}
          className="bg-surface border-line absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-sm border py-1 shadow-lg"
        >
          {choices.map((choice, index) => (
            <li
              key={choice ?? "any"}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={choice === value}
              // Not a button, because the press must not move focus off the
              // trigger — the trigger is what the keyboard is talking to.
              //
              // The choice is made on click, not on pointerdown. On a phone a
              // finger that lands on a row may be about to scroll the list, and
              // pointerdown fires before the browser knows which; choosing
              // there made the list impossible to scroll, because every touch
              // picked a country and closed it. A click is only fired for a tap
              // that did not turn into a scroll. The mousedown default is
              // still prevented, which is what keeps focus on the trigger.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(index)}
              onPointerEnter={() => setActive(index)}
              className={`flex h-11 cursor-pointer items-center gap-2 px-3 ${
                index === active ? "bg-raised" : ""
              } ${choice === value ? "text-accent" : ""} ${
                // The line under the reader's own countries. The "any" row
                // stands above them when there is one, so it is counted in.
                preferred > 0 &&
                index === preferred - 1 + (anyLabel === undefined ? 0 : 1) &&
                index < choices.length - 1
                  ? "border-line border-b"
                  : ""
              }`}
            >
              {choice !== null && <Flag countryCode={choice} />}
              <span className="truncate">{label(choice)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
